import React from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';
import { store } from './store';
import './i18n/config';
import './utils/axiosConfig';
import { AuthProvider } from './contexts/AuthContext';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import LeaveApplication from './pages/LeaveApplication';
import LeaveHistory from './pages/LeaveHistory';
import LeaveBalance from './pages/LeaveBalance';
import ExtraWorkingHoursApplication from './pages/ExtraWorkingHoursApplication';
import ExtraWorkingHoursHistory from './pages/ExtraWorkingHoursHistory';
import OutdoorWorkApplication from './pages/OutdoorWorkApplication';
import OutdoorWorkHistory from './pages/OutdoorWorkHistory';
import ApprovalList from './pages/ApprovalList';
import ApprovalDetail from './pages/ApprovalDetail';
import ApprovalHistory from './pages/ApprovalHistory';
import OutdoorWorkBulkApproval from './pages/OutdoorWorkBulkApproval';
import AdminUsers from './pages/AdminUsers';
import AdminLeaveTypes from './pages/AdminLeaveTypes';
import AdminBalances from './pages/AdminBalances';
import AdminAnnualLeaveBulk from './pages/AdminAnnualLeaveBulk';
import AdminEntitlementBulk from './pages/AdminEntitlementBulk';
import AdminSicknessAllowance from './pages/AdminSicknessAllowance';
import AdminDepartments from './pages/AdminDepartments';
import AdminPositions from './pages/AdminPositions';
import AdminShiftDutyRoles from './pages/AdminShiftDutyRoles';
import AdminGroups from './pages/AdminGroups';
import AdminPaperFlow from './pages/AdminPaperFlow';
import AdminExtraWorkingHoursPaperFlow from './pages/AdminExtraWorkingHoursPaperFlow';
import AdminOutdoorWorkPaperFlow from './pages/AdminOutdoorWorkPaperFlow';
import AdminStores from './pages/AdminStores';
import ChangePassword from './pages/ChangePassword';
import HRDocumentUpload from './pages/HRDocumentUpload';
import EmployeeDocuments from './pages/EmployeeDocuments';
import FormLibrary from './pages/FormLibrary';
import DepartmentGroupBalances from './pages/DepartmentGroupBalances';
import MyApplications from './pages/MyApplications';
import MyApprovals from './pages/MyApprovals';
import SystemMaintenance from './pages/SystemMaintenance';
import ManualApproval from './pages/ManualApproval';
import PublicHolidayManagement from './pages/PublicHolidayManagement';
import Tools from './pages/Tools';
import CashSettlement from './pages/CashSettlement';
import Schedule from './pages/Schedule';
import GroupLeaveCalendar from './pages/GroupLeaveCalendar';
import Attendance from './pages/Attendance';
import ShiftManagement from './pages/ShiftManagement';
import ShiftUserSchedules from './pages/ShiftUserSchedules';
import MyRoster from './pages/MyRoster';
import MyAttendance from './pages/MyAttendance';
import MyContacts from './pages/MyContacts';
import MonthlyAttendanceSummary from './pages/MonthlyAttendanceSummary';
import MonthlyAttendanceReport from './pages/MonthlyAttendanceReport';
import MonthlyAttendanceReportExport from './pages/MonthlyAttendanceReportExport';
import MonthlyAttendanceReportBulk from './pages/MonthlyAttendanceReportBulk';
import StoreDirectory from './pages/StoreDirectory';
import YearManagement from './pages/YearManagement';
import StoreHoursReport from './pages/StoreHoursReport';
import StoreWageCostReport from './pages/StoreWageCostReport';
// 設定後端 API 地址
// axios.defaults.baseURL = 'http://3.1.139.29:1689';
axios.defaults.baseURL = 'https://sp.bstaffhk.com';
// axios.defaults.baseURL = 'http://ec2-3-1-139-29.ap-southeast-1.compute.amazonaws.com:1689';
// axios.defaults.baseURL = 'http://localhost:1689';
// axios.defaults.baseURL = 'http://172.31.132.46:1689';
// axios.defaults.baseURL = 'http://192.168.3.4:1689';

const theme = createTheme({
  palette: {  
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Provider store={store}>
      <AuthProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
            <Route path="/my-applications" element={<PrivateRoute><MyApplications /></PrivateRoute>} />
            <Route path="/my-approvals" element={<PrivateRoute><MyApprovals /></PrivateRoute>} />
            <Route path="/system-maintenance" element={<PrivateRoute><SystemMaintenance /></PrivateRoute>} />
            <Route path="/manual-approval" element={<PrivateRoute><ManualApproval /></PrivateRoute>} />
            <Route path="/admin/public-holidays" element={<PrivateRoute><PublicHolidayManagement /></PrivateRoute>} />
            <Route path="/leave/apply" element={<PrivateRoute><LeaveApplication /></PrivateRoute>} />
            <Route path="/leave/history" element={<PrivateRoute><LeaveHistory /></PrivateRoute>} />
            <Route path="/leave/balance" element={<PrivateRoute><LeaveBalance /></PrivateRoute>} />
            <Route path="/extra-working-hours/apply" element={<PrivateRoute><ExtraWorkingHoursApplication /></PrivateRoute>} />
            <Route path="/extra-working-hours/history" element={<PrivateRoute><ExtraWorkingHoursHistory /></PrivateRoute>} />
            <Route path="/outdoor-work/apply" element={<PrivateRoute><OutdoorWorkApplication /></PrivateRoute>} />
            <Route path="/outdoor-work/history" element={<PrivateRoute><OutdoorWorkHistory /></PrivateRoute>} />
            <Route path="/approval/list" element={<PrivateRoute><ApprovalList /></PrivateRoute>} />
            <Route path="/approval/history" element={<PrivateRoute><ApprovalHistory /></PrivateRoute>} />
            <Route path="/approval/:id" element={<PrivateRoute><ApprovalDetail /></PrivateRoute>} />
            <Route path="/approval/outdoor-work/bulk/:applicantId" element={<PrivateRoute><OutdoorWorkBulkApproval /></PrivateRoute>} />
            <Route path="/admin/paper-flow" element={<PrivateRoute><AdminPaperFlow /></PrivateRoute>} />
            <Route path="/admin/extra-working-hours-paper-flow" element={<PrivateRoute><AdminExtraWorkingHoursPaperFlow /></PrivateRoute>} />
            <Route path="/admin/outdoor-work-paper-flow" element={<PrivateRoute><AdminOutdoorWorkPaperFlow /></PrivateRoute>} />
            <Route path="/admin/users" element={<PrivateRoute><AdminUsers /></PrivateRoute>} />
            <Route path="/admin/leave-types" element={<PrivateRoute><AdminLeaveTypes /></PrivateRoute>} />
            <Route path="/admin/balances" element={<PrivateRoute><AdminBalances /></PrivateRoute>} />
            <Route path="/admin/annual-leave-bulk" element={<PrivateRoute><AdminAnnualLeaveBulk /></PrivateRoute>} />
            <Route path="/admin/birthday-leave-bulk" element={<PrivateRoute><AdminEntitlementBulk /></PrivateRoute>} />
            <Route path="/admin/paid-sick-leave-bulk" element={<PrivateRoute><AdminEntitlementBulk /></PrivateRoute>} />
            <Route path="/admin/sickness-allowance" element={<PrivateRoute><AdminSicknessAllowance /></PrivateRoute>} />
            <Route path="/admin/departments" element={<PrivateRoute><AdminDepartments /></PrivateRoute>} />
            <Route path="/admin/positions" element={<PrivateRoute><AdminPositions /></PrivateRoute>} />
            <Route path="/admin/shift-duty-roles" element={<PrivateRoute><AdminShiftDutyRoles /></PrivateRoute>} />
            <Route path="/admin/stores" element={<PrivateRoute><AdminStores /></PrivateRoute>} />
            <Route path="/admin/groups" element={<PrivateRoute><AdminGroups /></PrivateRoute>} />
            <Route path="/documents/upload" element={<PrivateRoute><HRDocumentUpload /></PrivateRoute>} />
            <Route path="/documents/my" element={<PrivateRoute><EmployeeDocuments /></PrivateRoute>} />
            <Route path="/tools" element={<PrivateRoute><Tools /></PrivateRoute>} />
            <Route path="/tools/transaction-settlement" element={<PrivateRoute><CashSettlement /></PrivateRoute>} />
            <Route path="/tools/cash-settlement" element={<Navigate to="/tools/transaction-settlement" replace />} />
            <Route path="/form-library" element={<PrivateRoute><FormLibrary /></PrivateRoute>} />
            <Route path="/external-links" element={<Navigate to="/" replace />} />
            <Route path="/department-group-balances" element={<PrivateRoute><DepartmentGroupBalances /></PrivateRoute>} />
            <Route path="/change-password" element={<PrivateRoute><ChangePassword /></PrivateRoute>} />
            <Route path="/schedule" element={<PrivateRoute><Schedule /></PrivateRoute>} />
            <Route path="/group-leave-calendar" element={<PrivateRoute><GroupLeaveCalendar /></PrivateRoute>} />
            <Route path="/attendance" element={<PrivateRoute><Attendance /></PrivateRoute>} />
            <Route path="/shift-management" element={<PrivateRoute><ShiftManagement /></PrivateRoute>} />
            <Route path="/shift-management/user-schedules" element={<PrivateRoute><ShiftUserSchedules /></PrivateRoute>} />
            <Route path="/shift-management/daily-duties" element={<Navigate to="/schedule" replace />} />
            <Route path="/shift-management/store-hours-report" element={<PrivateRoute><StoreHoursReport /></PrivateRoute>} />
            <Route path="/shift-management/store-wage-cost" element={<PrivateRoute><StoreWageCostReport /></PrivateRoute>} />
            <Route path="/my-roster" element={<PrivateRoute><MyRoster /></PrivateRoute>} />
            <Route path="/my-attendance" element={<PrivateRoute><MyAttendance /></PrivateRoute>} />
            <Route path="/my-contacts" element={<PrivateRoute><MyContacts /></PrivateRoute>} />
            <Route path="/tools/stores" element={<PrivateRoute><StoreDirectory /></PrivateRoute>} />
            <Route path="/monthly-attendance-summary" element={<PrivateRoute><MonthlyAttendanceSummary /></PrivateRoute>} />
            <Route path="/shift-management/monthly-report-bulk" element={<PrivateRoute><MonthlyAttendanceReportBulk /></PrivateRoute>} />
            <Route path="/monthly-attendance-report/:id" element={<PrivateRoute><MonthlyAttendanceReport /></PrivateRoute>} />
            <Route path="/monthly-attendance-report" element={<PrivateRoute><MonthlyAttendanceReport /></PrivateRoute>} />
            <Route path="/monthly-attendance-report-export" element={<PrivateRoute><MonthlyAttendanceReportExport /></PrivateRoute>} />
            <Route path="/admin/years" element={<PrivateRoute><YearManagement /></PrivateRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
      </Provider>
    </ThemeProvider>
  );
}

export default App;

